'use client';

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'framer-motion';
import { ModalDialog } from '../components/ui/ModalDialog';

type ModalType = 'danger' | 'warning' | 'info' | 'success';

type ModalOptions = {
	title?: string;
	message: string;
	type?: ModalType;
	confirmText?: string;
	cancelText?: string;
};

type ModalContextValue = {
	showConfirm: (options: ModalOptions) => Promise<boolean>;
	showAlert: (options: ModalOptions | string) => Promise<void>;
};

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export function ModalProvider({ children }: { children: React.ReactNode }) {
	const [state, setState] = useState<{
		isOpen: boolean;
		mode: 'alert' | 'confirm';
		options: ModalOptions;
	}>({
		isOpen: false,
		mode: 'alert',
		options: { message: '', title: '', type: 'info' },
	});

	const resolveRef = useRef<((value: boolean) => void) | null>(null);

	const showConfirm = useCallback((options: ModalOptions): Promise<boolean> => {
		return new Promise((resolve) => {
			setState({
				isOpen: true,
				mode: 'confirm',
				options: {
					title: options.title || 'Confirm Action',
					type: options.type || 'warning',
					confirmText: options.confirmText || 'Confirm',
					cancelText: options.cancelText || 'Cancel',
					message: options.message,
				},
			});
			resolveRef.current = resolve;
		});
	}, []);

	const showAlert = useCallback((opts: ModalOptions | string): Promise<void> => {
		return new Promise((resolve) => {
			const options = typeof opts === 'string' ? { message: opts } : opts;
			setState({
				isOpen: true,
				mode: 'alert',
				options: {
					title: options.title || 'Notification',
					type: options.type || 'info',
					confirmText: options.confirmText || 'OK',
					message: options.message,
				},
			});
			// For alert, we just resolve when closed
			resolveRef.current = () => resolve(); 
		});
	}, []);

	const handleConfirm = useCallback(() => {
		setState((prev) => ({ ...prev, isOpen: false }));
		if (resolveRef.current) {
			resolveRef.current(true);
			resolveRef.current = null;
		}
	}, []);

	const handleCancel = useCallback(() => {
		setState((prev) => ({ ...prev, isOpen: false }));
		if (resolveRef.current) {
			if (state.mode === 'confirm') {
				resolveRef.current(false);
			} else {
				resolveRef.current(true); // Alert close acts as resolve
			}
			resolveRef.current = null;
		}
	}, [state.mode]);

	return (
		<ModalContext.Provider value={{ showConfirm, showAlert }}>
			{children}
			{typeof document !== 'undefined' &&
				createPortal(
					<AnimatePresence>
						{state.isOpen && (
							<ModalDialog
								isOpen={state.isOpen}
								title={state.options.title!}
								message={state.options.message}
								type={state.options.type}
								confirmText={state.options.confirmText}
								cancelText={state.options.cancelText}
								onConfirm={handleConfirm}
								onCancel={state.mode === 'confirm' ? handleCancel : undefined}
							/>
						)}
					</AnimatePresence>,
					document.body
				)}
		</ModalContext.Provider>
	);
}

export function useModal() {
	const ctx = useContext(ModalContext);
	if (!ctx) throw new Error('useModal must be used within ModalProvider');
	return ctx;
}
